import React, { useMemo } from "react";
import { Alert, Button, Empty, Popconfirm, Space, Table, Tag, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import { formatRateAmount, formatRateDate, formatRateValue } from "./rateUi";
import { useTranslation } from "../../i18n/LanguageProvider";

function renderCompactText(value, maxWidth = 220) {
    const text = value || "-";

    return (
        <div
            title={text}
            style={{
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {text}
        </div>
    );
}

/**
 * Ханш table.
 *
 * Only the six business columns are rendered - Name, Currency, Rate, Institution, Amount and
 * Executed date. The row id, the import source and the audit fields stay out of the UI.
 */
function RateTable({
    rows,
    loading,
    error,
    pagination,
    onChange,
    currencyOptions = [],
    institutionOptions = [],
    activeFilters = {},
    canEdit,
    canDelete,
    onEdit,
    onDelete,
    onReload,
}) {
    const { t } = useTranslation();

    const columns = useMemo(
        () => [
            {
                title: t("col.name"),
                dataIndex: "name",
                key: "name",
                width: 280,
                fixed: "left",
                sorter: true,
                render: (value) => renderCompactText(value, 270),
            },
            {
                title: t("col.currency"),
                dataIndex: "currency",
                key: "currency",
                width: 120,
                sorter: true,
                filters: currencyOptions.map((item) => ({ text: item, value: item })),
                filteredValue: activeFilters.currency || null,
                filterSearch: true,
                render: (value) => (value ? <Tag color="blue">{value}</Tag> : "-"),
            },
            {
                title: t("col.rate"),
                dataIndex: "rate",
                key: "rate",
                width: 140,
                align: "right",
                sorter: true,
                render: (value) => <span style={{ fontWeight: 600 }}>{formatRateValue(value)}</span>,
            },
            {
                title: t("col.institution"),
                dataIndex: "institution",
                key: "institution",
                width: 180,
                sorter: true,
                filters: institutionOptions.map((item) => ({ text: item, value: item })),
                filteredValue: activeFilters.institution || null,
                filterSearch: true,
                render: (value) => (value ? <Tag color="geekblue">{value}</Tag> : "-"),
            },
            {
                title: t("col.amount"),
                dataIndex: "amount",
                key: "amount",
                width: 180,
                align: "right",
                sorter: true,
                render: (value) => <span style={{ fontWeight: 600 }}>{formatRateAmount(value)}</span>,
            },
            {
                title: t("col.executedDate"),
                dataIndex: "executedDate",
                key: "executedDate",
                width: 170,
                sorter: true,
                defaultSortOrder: "descend",
                render: (value) => formatRateDate(value),
            },
            ...(canEdit || canDelete
                ? [
                      {
                          title: t("col.actions"),
                          key: "actions",
                          width: 110,
                          fixed: "right",
                          render: (_, record) => (
                              <Space wrap size={6}>
                                  {canEdit && (
                                      <Tooltip title={t("action.edit")}>
                                          <Button
                                              size="small"
                                              aria-label={t("action.edit")}
                                              icon={<EditOutlined />}
                                              onClick={() => onEdit(record)}
                                          />
                                      </Tooltip>
                                  )}

                                  {canDelete && (
                                      <Popconfirm
                                          title={t("delete.confirmTitle")}
                                          description={t("rate.delete.confirmBody")}
                                          okText={t("action.delete")}
                                          cancelText={t("action.cancel")}
                                          okButtonProps={{ danger: true }}
                                          onConfirm={() => onDelete(record)}
                                      >
                                          <Tooltip title={t("action.delete")}>
                                              <Button
                                                  size="small"
                                                  danger
                                                  aria-label={t("action.delete")}
                                                  icon={<DeleteOutlined />}
                                              />
                                          </Tooltip>
                                      </Popconfirm>
                                  )}
                              </Space>
                          ),
                      },
                  ]
                : []),
        ],
        [canEdit, canDelete, onEdit, onDelete, currencyOptions, institutionOptions, activeFilters, t]
    );

    if (error) {
        return (
            <Alert
                type="error"
                showIcon
                message={t("rate.loadError")}
                description={error}
                action={
                    <Button size="small" onClick={onReload}>
                        {t("action.retry")}
                    </Button>
                }
            />
        );
    }

    return (
        <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            onChange={onChange}
            size="middle"
            scroll={{ x: canEdit || canDelete ? 1180 : 1070 }}
            pagination={pagination}
            locale={{
                emptyText: loading ? (
                    " "
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("rate.empty")} />
                ),
            }}
        />
    );
}

export default RateTable;
